import { useState } from "react";
import { Button, Input } from "@heroui/react";
import {
  earlyForInput,
  earlyFromInput,
  earlyProblem,
  repeatProblem,
} from "../../lib/conditions";
import {
  exceptionRule,
  finallyRule,
  moveRule,
  setProfilePools,
  setProfileQueues,
} from "../../lib/profilesRules";
import { nameIn } from "../../lib/staffText";
import type {
  CallRule,
  OperationDoc,
  ProfilePool,
  ProfileProblem,
  ProfileWorkProfile,
} from "../../lib/types";
import { Icon } from "../../ui/icons";
import { useStaffText } from "../../ui/StaffText";
import { Num } from "../parts";
import { Confirm, Toggle } from "../sectionKit";
import { PinLines, pinsUnder, type Pin } from "./ConditionRow";
import { OrderedPick } from "./OrderedPick";
import {
  OrderFields,
  QueueTag,
  RuleBlock,
  RULES_MAX,
  type QueueLabel,
} from "./RuleBlock";
import { Notice } from "../settingsKit";
import { ProfileModal } from "./sheetParts";

const POOLS: readonly ProfilePool[] = ["desk", "branch", "staff"];
// No wrap-up or recall gap: the server ignores wrapUpSec and recallIntervalSec.
const TIMERS = [
  "noShowSec",
  "repeatCallSec",
] as const;
const PLACED =
  /^(name|queueIds(\[\d+\])?|pools(\[\d+\])?|rules|noShowSec|repeatCallSec|appointmentEarlySec)$/;

/** A work profile: what it serves, Finally call by, the Except-when rules in order, and its timers. */
export function WorkProfileSheet({
  doc,
  profile,
  problems,
  readOnly,
  onApply,
  onRemove,
  onClose,
}: {
  doc: OperationDoc;
  profile: ProfileWorkProfile;
  problems: ProfileProblem[];
  readOnly: boolean;
  onApply: (w: ProfileWorkProfile) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { lang, say } = useStaffText();
  // Every profile has its Finally rule; one that arrives without gets a fresh one.
  const [w, setW] = useState<ProfileWorkProfile>(() =>
    profile.rules?.length ? profile : { ...profile, rules: [finallyRule()] },
  );
  const pools = w.pools ?? [];
  const rules = w.rules;

  // Pins are this card's; their paths start workProfiles[i].
  const pins: Pin[] = problems.map((problem) => ({
    problem,
    field: problem.path.replace(/^workProfiles\[\d+\]\.?/, ""),
  }));
  const ruleAt = (f: string) => Number(/^rules\[(\d+)\]/.exec(f)?.[1] ?? -1);
  const top = pins.filter(
    (p) =>
      !PLACED.test(p.field) &&
      !(ruleAt(p.field) >= 0 && ruleAt(p.field) < rules.length),
  );

  const labels: QueueLabel[] = [...doc.queues]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((q) => ({
      id: q.id,
      letter: q.letter,
      name: nameIn(q.name, lang, q.code),
    }));
  const served = w.queueIds
    .map((id) => labels.find((q) => q.id === id))
    .filter((q): q is QueueLabel => !!q);

  const setRule = (i: number, next: CallRule) =>
    setW({ ...w, rules: rules.map((r, at) => (at === i ? next : r)) });
  const togglePool = (p: ProfilePool, on: boolean) =>
    setW(
      setProfilePools(
        w,
        POOLS.filter((x) => (x === p ? on : pools.includes(x))),
      ),
    );

  const repeatBad = repeatProblem(w.repeatCallSec, lang);
  const earlyBad = earlyProblem(w.appointmentEarlySec, lang);
  const finale = rules[0];

  return (
    <ProfileModal
      title={w.name.trim() || say("profiles.workProfile")}
      readOnly={readOnly}
      onClose={onClose}
      onDone={() => onApply(w)}
      wide
      extra={
        readOnly ? undefined : (
          <Confirm
            ask={say("common.remove")}
            question={say("profiles.wp.removeAsk")}
            yes={say("common.remove")}
            keep={say("profiles.sheet.keep")}
            onYes={onRemove}
          />
        )
      }
    >
      <fieldset className="pf-wp" disabled={readOnly}>
        {top.length > 0 && (
          <Notice className="pf-pins">
            <PinLines pins={top} at={() => true} />
          </Notice>
        )}

        <label className="field">
          <span>{say("common.name")}</span>
          <Input
            fullWidth
            autoFocus
            value={w.name}
            aria-invalid={pins.some((p) => p.field === "name") || undefined}
            onChange={(e) => setW({ ...w, name: e.target.value })}
          />
          <PinLines pins={pins} at={(f) => f === "name"} />
        </label>

        <fieldset className="field">
          <legend>{say("profiles.wp.queues")}</legend>
          <OrderedPick
            all={labels.map((q) => ({
              id: q.id,
              label: <QueueTag q={q} />,
              name: q.name,
            }))}
            picked={w.queueIds}
            onChange={(next) => setW(setProfileQueues(w, next))}
            readOnly={readOnly}
            empty={say("profiles.wp.noQueues")}
          />
          <PinLines pins={pins} at={(f) => /^queueIds(\[\d+\])?$/.test(f)} />
        </fieldset>

        <fieldset className="field">
          <legend>{say("profiles.wp.pools")}</legend>
          <div className="pf-wp__pools">
            {POOLS.map((p) => (
              <Toggle
                key={p}
                title={say(`profiles.wp.pool.${p}`)}
                checked={pools.includes(p)}
                disabled={readOnly}
                onChange={(on) => togglePool(p, on)}
              />
            ))}
          </div>
          <PinLines pins={pins} at={(f) => /^pools(\[\d+\])?$/.test(f)} />
        </fieldset>

        <section
          className="pf-wp__block"
          aria-label={say("profiles.wp.exceptWhen")}
        >
          <h3>{say("profiles.wp.exceptWhen")}</h3>
          {rules.length > 1 && (
            <ol className="pf-wp__rules">
              {rules.slice(1).map((r, k) => {
                const i = k + 1;
                return (
                  <RuleBlock
                    key={r.id}
                    rule={r}
                    index={i}
                    count={rules.length}
                    queues={served}
                    pools={pools}
                    pins={pinsUnder(pins, `rules[${i}]`)}
                    readOnly={readOnly}
                    onChange={(next) => setRule(i, next)}
                    onMove={(by) => setW(moveRule(w, i, i + by))}
                    onRemove={() =>
                      setW({ ...w, rules: rules.filter((_, at) => at !== i) })
                    }
                  />
                );
              })}
            </ol>
          )}
          <PinLines pins={pins} at={(f) => f === "rules"} />
          {!readOnly && (
            <div className="picklist__add">
              <Button
                variant="outline"
                size="sm"
                isDisabled={rules.length >= RULES_MAX || w.queueIds.length === 0}
                onPress={() =>
                  setW({ ...w, rules: [...rules, exceptionRule(w.queueIds)] })
                }
              >
                <Icon name="plus" size={14} /> {say("profiles.wp.addRule")}
              </Button>
            </div>
          )}
        </section>

        {finale && (
          <section
            className="pf-wp__block pf-wp__finally"
            aria-label={say("profiles.wp.finally")}
          >
            <h3>{say("profiles.wp.finally")}</h3>
            <OrderFields
              rule={finale}
              shareQueues={served}
              singleAllowed={w.queueIds.length + pools.length === 1}
              pins={pinsUnder(pins, "rules[0]")}
              readOnly={readOnly}
              onChange={(next) => setRule(0, next)}
            />
            <PinLines
              pins={pinsUnder(pins, "rules[0]")}
              at={(f) =>
                f !== "order" &&
                f !== "primaryResource" &&
                !(
                  f === "shares" &&
                  finale.order === "fair_share" &&
                  served.length > 0
                )
              }
            />
          </section>
        )}

        <fieldset className="field">
          <legend>{say("profiles.wp.timers")}</legend>
          <div className="pf-wp__timers">
            {TIMERS.map((field) => (
              <label key={field} className="field">
                <span title={field === "repeatCallSec" ? say("profiles.wp.repeatHint") : undefined}>{say(`profiles.wp.${field}`)}</span>
                <Num
                  value={w[field]}
                  min={0}
                  max={3600}
                  disabled={readOnly}
                  onChange={(n) => setW({ ...w, [field]: n })}
                />
                {field === "repeatCallSec" && repeatBad && <p className="field-error pf-wp__error">{repeatBad}</p>}
                <PinLines pins={pins} at={(f) => f === field} />
              </label>
            ))}
            <label className="field">
              <span title={say("profiles.wp.earlyHint")}>{say("profiles.wp.early")}</span>
              <Input
                className="setup-num"
                type="number"
                inputMode="numeric"
                min={0}
                value={earlyForInput(w.appointmentEarlySec)}
                onChange={(e) => {
                  const sec = earlyFromInput(e.target.value);
                  const next = { ...w };
                  if (sec === null) delete next.appointmentEarlySec;
                  else next.appointmentEarlySec = sec;
                  setW(next);
                }}
              />
              {earlyBad && <p className="field-error pf-wp__error">{earlyBad}</p>}
              <PinLines pins={pins} at={(f) => f === "appointmentEarlySec"} />
            </label>
          </div>
        </fieldset>
      </fieldset>
    </ProfileModal>
  );
}
